/*
  GFM の自動リンク (https://… や www.…) を、URL に使える ASCII の字までに止める。

  remark-gfm の自動リンクは、空白と < が来るまでを URL として読む。空白を置かない日本語の文では、
  「www.example.co.jp」なら、いちばん右の…」のように、URL のあとの文の終わりまでが 1 本のリンクになる。
  ここでは、自動リンクが作ったリンクの文を、最初の ASCII 以外の字で切り、残りを素の文に戻す。

  自動リンクだけを直す。見分け方は次の通り。
  - www. の自動リンクは字のノードの置き換えで作られ、位置 (position) が無い。https:// の自動リンクは位置を持つが、
    本文の字の長さと位置の幅が同じになる (<URL> は 2 字、[文](URL) は 4 字以上広い)。位置の幅が広いリンクは書き手が書いたので触らない
  - 文が URL と同じ字 (www. のときは http:// を足した字) のリンクだけを対象にする
  切ったあと、末尾の句読点と、対の無い閉じ括弧は URL に含めない (GFM が末尾でやるのと同じ決まり)。
  本文の markdown は書き換えず、描くときの木だけを直す。
*/
type Offset = { offset?: number };
type Node = {
  type: string;
  url?: string;
  title?: string | null;
  value?: string;
  children?: Node[];
  position?: { start: Offset; end: Offset };
};

/** 書き手が書いたリンク ([文](URL)、<URL>) ではなく、自動リンクか。位置の幅が文の長さと同じなら自動リンク */
function isAutolinkLiteral(node: Node, text: string): boolean {
  const pos = node.position;
  if (!pos) return true;
  const { start, end } = pos;
  return typeof start.offset === "number" && typeof end.offset === "number" && end.offset - start.offset === text.length;
}

const NON_ASCII = /[^\x00-\x7F]/;

/** URL の末尾から、GFM が URL に含めない字 (句読点、対の無い閉じ括弧) を落とす */
function trimUrlEnd(text: string): string {
  let t = text;
  for (;;) {
    const before = t;
    t = t.replace(/[?!.,:;*_~'"]+$/, "");
    if (t.endsWith(")")) {
      const open = (t.match(/\(/g) ?? []).length;
      const close = (t.match(/\)/g) ?? []).length;
      if (close > open) t = t.slice(0, -1);
    }
    if (t === before) return t;
  }
}

function fix(parent: Node): void {
  const kids = parent.children;
  if (!kids) return;
  for (let i = 0; i < kids.length; i++) {
    const node = kids[i];
    const only = node.children?.length === 1 ? node.children[0] : null;
    if (
      node.type === "link" &&
      !node.title &&
      only?.type === "text" &&
      typeof only.value === "string" &&
      NON_ASCII.test(only.value) &&
      (node.url === only.value || node.url === `http://${only.value}`) &&
      isAutolinkLiteral(node, only.value)
    ) {
      const text = only.value;
      const cut = text.search(NON_ASCII);
      const head = trimUrlEnd(text.slice(0, cut));
      const rest = text.slice(head.length);
      const prefix = node.url!.slice(0, node.url!.length - text.length);
      const next: Node[] = [];
      if (head) next.push({ ...node, url: prefix + head, children: [{ type: "text", value: head }] });
      next.push({ type: "text", value: head ? rest : text });
      kids.splice(i, 1, ...next);
      // 切った残りは素の文。隣の字のノードと混ざっても、見た目は変わらない
      i += next.length - 1;
      continue;
    }
    fix(node);
  }
}

/** remark-gfm のあとに置く。自動リンクを ASCII の URL の字までに止める */
export function remarkAsciiAutolink() {
  return (tree: Node) => fix(tree);
}
